import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import { useLazyGetShipContentQuery } from "../../../store/services/ContentService";
import { useLazyRetrieveReservationQuery } from "../../../store/services/ReservationService";
import { ModalState, showModal } from "../../../store/slices/modalSlice";
import { ReservationGuestPricingInterface } from "../../../store/slices/reservationSlice";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import MODAL from "../../../utils/constants/modal";
import { CURRENT_BOOKINGS, ROOT } from "../../../utils/constants/routes";
import {
  getCancellationPolicyDaysRange,
  getCancellationPolicyValue,
} from "../../../utils/helpers/cancellationPolicy";
import { currencyToFormat } from "../../../utils/helpers/currency";
import LoadingContainer from "../../containers/LoadingContainer";
import CustomCarousel from "../../shared/Carousel";
import CustomBreadcrumbs from "../../shared/CustomBreadcrumbs";
import SvgButton from "../../shared/SvgButton";

import styles from "./index.module.scss";
import { Flights } from "./molecules";
import { OptionalExtras } from "./molecules/OptionalExtras";

const normalizePricing = (pricing: ReservationGuestPricingInterface) => {
  return {
    fare: Number(pricing?.fare ?? 0),
    gft: Number(pricing?.gft ?? 0),
    extras: Number(pricing?.extras ?? 0),
    discount: Number(pricing?.discount ?? 0),
    total_price: Number(pricing?.total_price ?? 0),
  };
};

function Booking() {
  const [getReservation] = useLazyRetrieveReservationQuery();
  const [getShip] = useLazyGetShipContentQuery();
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { pnr = "" } = useParams();

  const { type: externalModalType } = useTypedSelector((state) => state.modal);

  const { market, account_type } = useTypedSelector((state) => state.agency);

  const { ship } = useTypedSelector((state) => state.content);

  const { agency: channel_partner, agent: agency } = useTypedSelector(
    (state) => state.session,
  );

  const {
    date_format,
    is_manual_payment_allowed,
    api_language: language,
    prefer_external_pnr,
    enable_cabin_edit,
    enable_cancel_booking,
  } = useTypedSelector((state) => state.environment);

  const reservation = useTypedSelector(
    (state) => state.reservation.reservation,
  );

  const [internalModalType, setInternalModalType] = useState(externalModalType);
  const [isLoading, setIsLoading] = useState(true);

  const displayedPNR = useMemo(() => {
    let output = reservation?.pnr;

    if (prefer_external_pnr) {
      output = reservation?.external_pnr ?? reservation?.pnr;
    }

    return output;
  }, [prefer_external_pnr, reservation.pnr, reservation.external_pnr]);

  const sailing = useMemo(() => {
    const sailings = reservation?.sailings;

    if (!Array.isArray(sailings) || sailings.length === 0) {
      return null;
    }

    const totalNights = sailings.reduce(
      (sum, s) => sum + Number(s.cruise_duration || 0),
      0,
    );

    const first = sailings[0];
    const last = sailings[sailings.length - 1];
    const sailingCodes = sailings.map((s) => s.sailing_code).filter(Boolean);

    const ships = Array.from(
      new Map(
        sailings.map((s) => [s.ship_code, `${s.ship_name} (${s.ship_code})`]),
      ).values(),
    );

    const route = `${first.itinerary?.[0]?.port_name ?? ""} to ${
      last.itinerary?.[last.itinerary.length - 1]?.port_name ?? ""
    }`;

    return {
      title: `${totalNights}-Night ${route}`,
      ships: ships.join(" and "),
      embark: `Embarking ${dayjs(first.embarkation_date).format(date_format)} ${totalNights} nights (${sailingCodes.join(
        " and ",
      )})`,
      ...first,
    };
  }, [reservation]);

  const uniqueGuests = useMemo(() => {
    const allGuests =
      reservation?.sailings
        ?.flatMap((s) => s.cabins)
        ?.flatMap((c) => c.guests) ?? [];

    const merged = new Map();

    for (const guest of allGuests) {
      const key =
        guest.loyalty_number?.trim() ||
        guest.external_code?.trim() ||
        `${guest.title}:${guest.given_name}:${guest.lastname}:${guest.date_of_birth}`;

      const existing = merged.get(key);
      const normalizedPricing = normalizePricing(guest.pricing);

      if (existing) {
        const existingPricing = normalizePricing(existing.pricing);

        existing.pricing = {
          fare: existingPricing.fare + normalizedPricing.fare,
          gft: existingPricing.gft + normalizedPricing.gft,
          extras: existingPricing.extras + normalizedPricing.extras,
          discount: existingPricing.discount + normalizedPricing.discount,
          total_price:
            existingPricing.total_price + normalizedPricing.total_price,
        };
      } else {
        merged.set(key, { ...guest, pricing: normalizedPricing });
      }
    }

    return Array.from(merged.values());
  }, [reservation]);

  const mergedItinerary = useMemo(() => {
    const sailings = reservation?.sailings;

    if (!Array.isArray(sailings) || sailings.length === 0) {
      return [];
    }

    return sailings.flatMap((s) => s.itinerary ?? []);
  }, [reservation]);

  const totalRemaining = useMemo(
    () => +reservation.total_price - +reservation.total_paid,
    [reservation],
  );

  const cancellationPolicies = useMemo<
    Array<{
      price_type: string;
      type: string;
      value: number;
      days_before_departure: number;
    }>
  >(
    () =>
      reservation.sailings
        .flatMap((sailing) =>
          sailing.cabins.flatMap((cabin) => cabin.cancellation_policy),
        )
        .filter((item) => item),
    [reservation],
  );

  const isFuture = useMemo(() => {
    const currentDate = new Date();
    const inputDate = new Date(sailing?.embarkation_date ?? "");

    currentDate.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate > currentDate;
  }, [sailing?.embarkation_date]);

  const handleShowModal =
    (
      type: ModalState["type"],
      data: ModalState["data"],
      title?: string | null,
    ) =>
    () => {
      dispatch(showModal({ type, data, title }));
      setInternalModalType(type);
    };

  const initShip = async () => {
    try {
      const sailings = reservation?.sailings ?? [];

      const codes = Array.from(
        new Set(sailings.map((s) => s.ship_code).filter(Boolean)),
      );

      await Promise.all(
        codes.map((ship_code) =>
          getShip({
            ship_code,
            language,
          }),
        ),
      );

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getReservation({ pnr, channel_partner, agency });
  }, []);

  useEffect(() => {
    const sailings = reservation?.sailings ?? [];
    const hasShips = sailings.some((s) => s.ship_code);

    if (hasShips) {
      initShip();
    }
  }, [reservation?.sailings]);

  useEffect(() => {
    if (internalModalType && !externalModalType) {
      setInternalModalType("");
      getReservation({ pnr, channel_partner, agency });
    }
  }, [externalModalType]);

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.crumbsAndButton}>
            <CustomBreadcrumbs
              crumbsList={[
                [ROOT, "HOME"],
                [CURRENT_BOOKINGS, "CURRENT BOOKINGS"],
                displayedPNR ?? "BOOKING",
              ]}
            />

            <button className={styles.backButton} onClick={() => navigate(-1)}>
              {t("Back to all bookings")}
            </button>
          </div>

          <div className={styles.bookingInfoContainer}>
            <div className={styles.topSection}>
              <img
                src={ship?.images?.[0].image ?? ""}
                className={styles.mainImage}
                alt="ship"
              />

              <div className={styles.reservationInfo}>
                <div className={styles.reservationItem}>
                  <div className={styles.paymentButtonContainer}>
                    <p className={styles.pnr}>{displayedPNR}</p>

                    <div className={styles.actionButtons}>
                      {is_manual_payment_allowed && (
                        <SvgButton
                          tooltip={t("Make Payment")}
                          icon="FiDollarSign"
                          iconClassName={styles.svgAction}
                          onClick={handleShowModal(
                            MODAL.MODAL_TYPES.PAYMENT,
                            reservation.pnr,
                          )}
                          isDisabled={
                            reservation.status === "cancelled" ||
                            account_type === "creditaccount" ||
                            totalRemaining === 0
                          }
                        />
                      )}

                      {enable_cancel_booking && (
                        <SvgButton
                          tooltip={t("Cancel Booking")}
                          icon="FiXCircle"
                          iconClassName={styles.svgAction}
                          onClick={handleShowModal(
                            MODAL.MODAL_TYPES.CANCEL_BOOKING,
                            reservation.pnr,
                          )}
                          isDisabled={
                            reservation.status === "cancelled" || !isFuture
                          }
                        />
                      )}
                    </div>
                  </div>

                  {sailing && (
                    <>
                      <p className={styles.cruiseName}>{sailing.title}</p>
                      <p className={styles.ship}>{sailing.ships}</p>
                      <p className={styles.embarking}>{sailing.embark}</p>
                    </>
                  )}
                </div>

                <div className={styles.reservationItem}>
                  <p className={styles.guestInfo_primary}>
                    {reservation?.lead_title ?? ""}{" "}
                    {reservation?.lead_given_name ?? ""}{" "}
                    {reservation?.lead_lastname ?? ""}
                  </p>

                  <p className={styles.guestInfo_secondary}>
                    {reservation?.lead_email ?? ""}
                  </p>

                  <p className={styles.guestInfo_secondary}>
                    {reservation?.lead_phone ?? ""}
                  </p>
                </div>

                <div className={styles.reservationItem}>
                  <p className={styles.priceInfo_price}>
                    {currencyToFormat(
                      Number(reservation.total_price ?? "0"),
                      reservation?.currency ?? "USD",
                    )}
                  </p>

                  <p className={styles.priceInfo_description}>
                    {sailing?.rate_code ?? ""}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <h2 className={styles.tableContainer_title}>{t("passengers")}</h2>

              <div className={styles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("name")}</th>
                      <th>{t("date of birth")}</th>
                      <th>{t("nationality")}</th>
                      <th>{t("price")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {uniqueGuests?.map((guest) => {
                      const {
                        uuid,
                        title,
                        given_name,
                        lastname,
                        date_of_birth,
                        nationality,
                        pricing,
                      } = guest;

                      return (
                        <tr key={uuid}>
                          <td>
                            <p>{`${title} ${given_name} ${lastname}`}</p>
                          </td>

                          <td>
                            <p>{dayjs(date_of_birth).format(date_format)}</p>
                          </td>

                          <td>
                            <p>{`${nationality ?? t("Not specified")}`}</p>
                          </td>

                          <td>
                            <p className={styles.textContent}>
                              <span>{`${t("Fare")}: `}</span>
                              {`${currencyToFormat(pricing.fare || "0", reservation.currency)}`}
                            </p>

                            <p className={styles.textContent}>
                              <span>{`${t("Taxes")}: `}</span>
                              {`${currencyToFormat(pricing.gft || "0", reservation.currency)}`}
                            </p>

                            <p className={styles.textContent}>
                              <span>{`${t("Extras")}: `}</span>
                              {`${currencyToFormat(pricing.extras || "0", reservation.currency)}`}
                            </p>

                            <p className={styles.textContent}>
                              <span>{`${t("Discount")}: `}</span>
                              {currencyToFormat(
                                pricing.discount,
                                reservation.currency,
                              )}
                            </p>

                            <p className={styles.textContent}>
                              <span>{`${t("Total")}: `}</span>
                              {currencyToFormat(
                                pricing.total_price || 0,
                                reservation.currency,
                              )}
                            </p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Flights />

            <div className={styles.tableContainer}>
              <h2 className={styles.tableContainer_title}>{t("cabins")}</h2>

              <div className={styles.info}>
                {(reservation?.sailings?.[0]?.cabins ?? []).map((cabin) => {
                  const {
                    guests,
                    deck_name = "",
                    cabin_number,
                    grade_code,
                  } = cabin;

                  const grade = ship.grades.find(
                    (el) => el?.code === grade_code,
                  );

                  return (
                    <div key={cabin.uuid} className={styles.cabin}>
                      <CustomCarousel
                        items={grade?.images.map((el) => el.image) ?? []}
                        className={styles.carousel}
                      />

                      <div className={styles.cabinInfo}>
                        <div className={styles.guestsContainer}>
                          <div className={styles.guests}>
                            {(guests ?? []).map((guest) => {
                              const { uuid, title, given_name, lastname } =
                                guest;

                              return (
                                <div key={uuid} className={styles.guest}>
                                  <p>{`${title} ${given_name} ${lastname}`}</p>
                                </div>
                              );
                            })}
                          </div>

                          {enable_cabin_edit && (
                            <SvgButton
                              icon="FiEdit"
                              isDisabled={
                                reservation.status === "cancelled" || !isFuture
                              }
                              iconClassName={styles.svgAction}
                              onClick={handleShowModal(
                                MODAL.MODAL_TYPES.EDIT_CABIN,
                                { cabin, sailing, reservation },
                                `${MODAL.MODAL_TYPES.EDIT_CABIN}: ${cabin.grade_code} / ${cabin.cabin_number}`,
                              )}
                            />
                          )}
                        </div>

                        <div className={styles.deckInfo}>
                          <p>
                            <span>{`${t("Deck")}: `}</span>

                            {deck_name?.split(" ")[1]}
                          </p>

                          <p>
                            <span>{`${t("Stateroom")}: `}</span>
                            {cabin_number}
                          </p>
                        </div>

                        <p className={styles.gradeName}>{grade?.name ?? ""}</p>

                        <p className={styles.gradeDescription}>
                          {grade?.description?.[0]?.description ?? ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.tableContainer}>
              <div>
                <h2 className={styles.tableContainer_title}>
                  {t("itinerary")}
                </h2>

                <p className={styles.tableContainer_subtitle}>
                  {sailing?.title ?? ""}
                </p>
              </div>

              <div className={styles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("date")}</th>
                      <th>{t("port")}</th>
                      <th>{t("arrive")}</th>
                      <th>{t("depart")}</th>
                      <th>{t("tender")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {mergedItinerary.map((item) => {
                      const {
                        date,
                        port_name,
                        port_code,
                        arrive_time,
                        depart_time,
                        tender,
                      } = item;

                      return (
                        <tr key={nanoid()}>
                          <td>
                            <p>{dayjs(date).format(date_format)}</p>
                          </td>

                          <td>
                            <p>{`${port_name} (${port_code})`}</p>
                          </td>

                          <td>
                            <p>{`${arrive_time ?? "-"}`}</p>
                          </td>

                          <td>
                            <p>{`${depart_time ?? "-"}`}</p>
                          </td>

                          <td>
                            <p>{t(tender ? "Yes" : "No")}</p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <OptionalExtras />

            <div className={styles.paymentContainer}>
              <div className={styles.tableContainer}>
                <div>
                  <h2 className={styles.tableContainer_title}>{t("agency")}</h2>

                  <p className={styles.tableContainer_subtitle}>
                    {reservation.agency_name}
                  </p>

                  <p
                    className={classNames(
                      styles.tableContainer_subtitle,
                      styles.uppercase,
                    )}>{`${t("market")}: ${market}`}</p>

                  <p
                    className={classNames(
                      styles.tableContainer_subtitle,
                      styles.uppercase,
                    )}>
                    {t("commission")}:{" "}
                    {currencyToFormat(
                      reservation.commission,
                      reservation.currency,
                    )}
                  </p>
                </div>
              </div>

              <div className={styles.tableContainer}>
                <div>
                  <h2 className={styles.tableContainer_title}>
                    {t("cancellation policies")}
                  </h2>

                  {cancellationPolicies.length !== 0 ? (
                    <div className={styles.tableWrapper}>
                      <table>
                        <thead>
                          <tr>
                            <th>{t("days before departure")}</th>
                            <th>{t("value")}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {(cancellationPolicies ?? []).map((item, index) => {
                            return (
                              <tr key={index}>
                                <td>
                                  <p>
                                    {getCancellationPolicyDaysRange(
                                      item?.days_before_departure,
                                    )}
                                  </p>
                                </td>

                                <td>
                                  <p>
                                    {getCancellationPolicyValue({
                                      ...item,
                                      currency: reservation.currency,
                                    })}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className={styles.tableContainer_subtitle}>
                      {`${t("There is currently no Cancellation Policies active on this booking.")}`}
                    </p>
                  )}
                </div>
              </div>

              <div className={styles.tableContainer}>
                <div>
                  <div className={styles.paymentButtonContainer}>
                    <h2 className={styles.tableContainer_title}>
                      {t("payment schedule")}
                    </h2>

                    {is_manual_payment_allowed && (
                      <SvgButton
                        icon="FiDollarSign"
                        iconClassName={styles.svgAction}
                        onClick={handleShowModal(
                          MODAL.MODAL_TYPES.PAYMENT,
                          reservation.pnr,
                        )}
                        isDisabled={
                          reservation.status === "cancelled" ||
                          account_type === "creditaccount" ||
                          totalRemaining === 0
                        }
                      />
                    )}
                  </div>

                  {Number(reservation.next_payment_outstanding) > 0 &&
                    Boolean(reservation.next_payment_due) && (
                      <p className={styles.tableContainer_subtitle}>
                        {`${t("Next payment of")} ${currencyToFormat(Number(reservation?.next_payment_outstanding ?? "0"), reservation.currency)} ${t("on")} ${dayjs(reservation?.next_payment_due ?? null).format(date_format)}`}
                      </p>
                    )}
                </div>

                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t("payment date")}</th>
                        <th>{t("payment amount")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(reservation?.payment_schedule ?? []).map((item) => {
                        const { due_date, amount } = item;

                        return (
                          <tr key={nanoid()}>
                            <td>
                              <p>{dayjs(due_date).format(date_format)}</p>
                            </td>

                            <td>
                              <p>
                                {currencyToFormat(
                                  Number(amount),
                                  reservation.currency,
                                )}
                              </p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.tableContainer}>
                <div>
                  <h2 className={styles.tableContainer_title}>
                    {t("transactions")}
                  </h2>

                  {reservation?.transactions?.[
                    reservation?.transactions?.length - 1
                  ] ? (
                    <p className={styles.tableContainer_subtitle}>
                      {`${t("Last transaction of")} ${currencyToFormat(Number(reservation?.transactions?.[reservation?.transactions?.length - 1]?.amount ?? "0"), reservation.currency)} ${t("was")} ${dayjs(reservation?.transactions?.[reservation?.transactions?.length - 1]?.made_on ?? null).format(date_format)}`}
                    </p>
                  ) : (
                    <p className={styles.tableContainer_subtitle}>
                      {t("No transactions found")}
                    </p>
                  )}
                </div>

                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t("transaction id")}</th>
                        <th>{t("amount")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {(reservation?.transactions ?? []).map((item) => {
                        const { card_clearance, amount } = item;

                        return (
                          <tr key={nanoid()}>
                            <td>
                              <p>{card_clearance?.transaction_id ?? ""}</p>
                            </td>

                            <td>
                              <p>
                                {currencyToFormat(
                                  Number(amount ?? "0"),
                                  reservation.currency,
                                )}
                              </p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadingContainer>
  );
}

export default Booking;
